import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Search, Filter, Clock, ChevronRight, X, Share2, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import EtkinliklerData from '../../data/Etkinlikler.json';

const EventsSection = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    try {
      // Etkinlikleri işle ve tarih sırala
      const processedEvents = EtkinliklerData
        .filter(event => event.baslik && event.tarih)
        .map(event => ({
          ...event,
          baslik: event.baslik.trim(),
          parsedDate: parseEventDate(event.tarih)
        }))
        .sort((a, b) => b.parsedDate - a.parsedDate); // En yeniden eskiye

      setEvents(processedEvents);
      setFilteredEvents(processedEvents.slice(0, 6)); // İlk 6 etkinlik
    } catch (error) {
      console.error('Etkinlik verileri yüklenirken hata:', error);
    }
  }, []);

  // Tarih parsing fonksiyonu
  const parseEventDate = (dateStr) => {
    try {
      // Farklı tarih formatlarını handle et
      if (dateStr.includes('.')) {
        const [datePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('.');
        return new Date(year, month - 1, day);
      }
      return new Date(dateStr);
    } catch {
      return new Date();
    }
  };

  // Tarih formatlama
  const formatDate = (date) => {
    return date.toLocaleDateString('tr-TR', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Etkinlik kategorisi renkleri
  const getCategoryColor = (category) => {
    const colors = {
      'Seminer': 'bg-blue-100 text-blue-800 border-blue-200',
      'Tören': 'bg-purple-100 text-purple-800 border-purple-200',
      'Sempozyum': 'bg-green-100 text-green-800 border-green-200',
      'Konferans': 'bg-orange-100 text-orange-800 border-orange-200',
      'etkinlik': 'bg-pink-100 text-pink-800 border-pink-200',
      'Söyleşi': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'sergi': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Anma Töreni': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Filtreleme
  useEffect(() => {
    let filtered = events;

    // Kategori filtresi
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => 
        event.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Zaman filtresi
    const now = new Date();
    if (selectedTimeFilter === 'upcoming') {
      filtered = filtered.filter(event => event.parsedDate >= now);
    } else if (selectedTimeFilter === 'past') {
      filtered = filtered.filter(event => event.parsedDate < now);
    }

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.baslik.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.konum.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredEvents(filtered.slice(0, 6));
  }, [selectedCategory, selectedTimeFilter, searchTerm, events]);

  // Benzersiz kategoriler
  const categories = [...new Set(events.map(event => event.category))];

  const handleEventClick = (event) => {
    // Fotoğrafı direkt aç
    if (event.imageURL) {
      window.open(event.imageURL, '_blank');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const addToCalendar = (event) => {
    const startDate = new Date(event.parsedDate);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Hacettepe Fen Fakültesi//Etkinlikler//TR\nBEGIN:VEVENT\nUID:${event.baslik}@hacettepe.edu.tr\nDTSTAMP:${new Date().toISOString().replace(/[-:.]/g, '')}Z\nDTSTART:${startDate.toISOString().replace(/[-:.]/g, '')}Z\nDTEND:${endDate.toISOString().replace(/[-:.]/g, '')}Z\nSUMMARY:${event.baslik}\nDESCRIPTION:${event.aciklama || ''}\nLOCATION:${event.konum || ''}\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.baslik}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Başlık ve Kontroller */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            <span className="text-indigo-600">{t('home.events.title')}</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('home.events.subtitle')}
          </p>

          {/* Filtreleme Kontrolleri */}
          <div className="flex flex-col lg:flex-row items-center justify-center space-y-4 lg:space-y-0 lg:space-x-6">
            
            {/* Arama Kutusu */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('home.events.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-80"
              />
            </div>

            {/* Filtre Butonu */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Filter size={20} />
              <span>{t('home.events.filter')}</span>
            </button>
          </div>

          {/* Filtre Seçenekleri */}
          {showFilters && (
            <div className="mt-6 p-6 bg-white rounded-2xl shadow-lg border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Kategori Filtresi */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">{t('home.events.category')}</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">{t('home.events.allCategories')}</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Zaman Filtresi */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">{t('home.events.time')}</label>
                  <select
                    value={selectedTimeFilter}
                    onChange={(e) => setSelectedTimeFilter(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">{t('home.events.allEvents')}</option>
                    <option value="upcoming">{t('home.events.upcomingEvents')}</option>
                    <option value="past">{t('home.events.pastEvents')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Etkinlikler Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredEvents.map((event, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-indigo-200"
            >
              {/* Etkinlik Görseli */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={event.imageURL}
                  alt={event.baslik}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Kategori Etiketi */}
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getCategoryColor(event.category)}`}>
                    {event.category}
                  </span>
                </div>

                {/* Tarih Kartı */}
                <div className="absolute bottom-4 right-4 bg-white rounded-lg p-2 shadow-lg">
                  <div className="text-center">
                    <div className="text-xs font-semibold text-gray-500 uppercase">
                      {formatDate(event.parsedDate).split(' ')[1]}
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {formatDate(event.parsedDate).split(' ')[0]}
                    </div>
                  </div>
                </div>
              </div>

              {/* Etkinlik İçeriği */}
              <div className="p-6">
                {/* Başlık */}
                <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                  {event.baslik}
                </h3>

                {/* Detaylar */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock size={16} className="mr-2 text-indigo-500" />
                    <span>{event.tarih}</span>
                  </div>
                  <div className="flex items-start text-sm text-gray-600">
                    <MapPin size={16} className="mr-2 mt-0.5 text-indigo-500 flex-shrink-0" />
                    <span className="line-clamp-2">{event.konum}</span>
                  </div>
                </div>

                {/* Detay Butonu */}
                <button
                  onClick={() => handleEventClick(event)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors group"
                >
                  <span>{t('home.events.detail')}</span>
                  <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Sonuç Bulunamadı */}
        {filteredEvents.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Calendar size={48} className="mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('home.events.notFound')}</h3>
            <p className="text-gray-500">{t('home.events.notFoundMessage')}</p>
          </div>
        )}

        {/* Tüm Etkinlikler Linki */}
        <div className="text-center mt-12">
          <Link
            to="/etkinlikler"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {t('home.events.viewAllEvents')}
            <ChevronRight size={20} className="ml-2" />
          </Link>
        </div>

        {/* Event Detail Modal */}
        {showModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="relative">
                <img
                  src={selectedEvent.imageURL}
                  alt={selectedEvent.baslik}
                  className="w-full h-64 object-cover rounded-t-2xl"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop';
                  }}
                />
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                >
                  <X size={20} />
                </button>
                <div className="absolute bottom-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getCategoryColor(selectedEvent.category)}`}>
                    {selectedEvent.category}
                  </span>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedEvent.baslik}</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center text-gray-600">
                    <Calendar size={20} className="mr-3 text-indigo-500" />
                    <span className="font-medium">{selectedEvent.tarih}</span>
                  </div>
                  
                  <div className="flex items-start text-gray-600">
                    <MapPin size={20} className="mr-3 mt-0.5 text-indigo-500 flex-shrink-0" />
                    <span className="font-medium">{selectedEvent.konum}</span>
                  </div>
                </div>

                {selectedEvent.aciklama && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.events.description')}</h3>
                    <p className="text-gray-600 leading-relaxed">{selectedEvent.aciklama}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => addToCalendar(selectedEvent)}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Calendar size={16} />
                    <span>{t('home.events.addToCalendar')}</span>
                  </button>

                  <button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: selectedEvent.baslik,
                          text: `${selectedEvent.baslik} - ${selectedEvent.tarih} - ${selectedEvent.konum}`,
                          url: window.location.href
                        });
                      }
                    }}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Share2 size={16} />
                    <span>{t('home.events.share')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsSection; 